import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { ALERTS_SLICE_NAME } from "./alerts.constants"
import {
  createAlertTicket,
  createCreditRequest,
  getAlertDetails,
  getAlertDetailsExport,
  getAlertLogs,
  getAlerts,
  getAlertsExport,
  getAlertsFilterOptions,
  getAlertsStatistics,
  pinAlert,
  updateAlert,
  updateAlertFromDetailsPage,
  updateAlertTransaction
} from "./alerts.thunks"

/* ------------- Initial State ------------- */

const columns = [
  { field_id: "transaction_id", label: "Transaction ID", active: true },
  { field_id: "alert_timestamp", label: "Alert date", active: true },
  { field_id: "transaction_timestamp", label: "Transaction date", active: true },
  { field_id: "alert_service", label: "Alert source", active: true },
  { field_id: "amount", label: "Order value", active: true },
  { field_id: "card_last4", label: "Card", active: true },
  { field_id: "country", label: "Country", active: true },
  { field_id: "bank_name", label: "Bank", active: true },
  { field_id: "alert_id", label: "Alert ID", active: true },
  { field_id: "arn", label: "ARN", active: true },
  { field_id: "auth_code", label: "Auth.Code", active: true },
  { field_id: "customer_email", label: "Customer email", active: true },
  { field_id: "customer_full_name", label: "Customer full name", active: true },
  { field_id: "alert_type", label: "Type", active: false },
  { field_id: "alert_reason", label: "Alert reason", active: false },
  { field_id: "merchant_descriptor", label: "Billing descriptor", active: false },
  { field_id: "payment_processor", label: "Payment processor", active: false },
  { field_id: "customer_unsubscribed", label: "Subscription canceled", active: false },
  { field_id: "original_alert_id", label: "Original alert", active: true }
]

const columnsAdmin = [
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "categories", label: "User type", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "business_url", label: "Business URL", active: true },
  { field_id: "merchant_descriptor", label: "Billing descriptor", active: true },
  { field_id: "alert_type", label: "Type", active: true },
  { field_id: "alert_reason", label: "Alert reason", active: true },
  { field_id: "transaction_timestamp", label: "Transaction date", active: true },
  { field_id: "alert_timestamp", label: "Alert date", active: true },
  { field_id: "alert_service", label: "Alert source", active: true },
  { field_id: "amount", label: "Order value", active: true },
  { field_id: "country", label: "Country", active: true },
  { field_id: "bank_name", label: "Bank", active: true },
  { field_id: "card_last4", label: "Card", active: true },
  { field_id: "payment_processor", label: "Payment processor", active: true },
  { field_id: "customer_email", label: "Customer email", active: true },
  { field_id: "customer_full_name", label: "Customer full name", active: true },
  { field_id: "alert_id", label: "Alert ID", active: true },
  { field_id: "transaction_id", label: "Transaction ID", active: true },
  { field_id: "arn", label: "ARN", active: true },
  { field_id: "auth_code", label: "Auth.Code", active: true },
  { field_id: "customer_unsubscribed", label: "Subscription canceled", active: true },
  { field_id: "user_visibility", label: "User visibility", active: true },
  { field_id: "paid_status", label: "Paid status", active: true },
  { field_id: "status_set", label: "Status set", active: true },
  { field_id: "credit_request_type", label: "Credit request", active: true },
  { field_id: "original_alert_id", label: "Original alert", active: true },
  { field_id: "billing_descriptor_status", label: "Descriptor status", active: true }
]

const initialState = {
  fetchingStatistics: false,
  statistics: {
    all: 0,
    ethoca: 0,
    cdrn: 0,
    rdr: 0
  },
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: [],
    attention_list: [],
    statistics: {
      all: 0,
      ethoca: 0,
      cdrn: 0,
      rdr: 0,
      attentions: 0
    }
  },
  defaultColumns: [{ field_id: "alert_status", label: "Status" }, ...columns],
  columns: [...columns],

  defaultColumnsAdmin: [{ field_id: "alert_status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    alert_timestamp: null,
    transaction_timestamp: null,
    alert_status: "all",
    alert_type: "all",
    alert_reason: "all",
    alert_service: "all",

    user_full_name: [],
    user_email: [],
    customer_email: [],
    customer_full_name: [],
    business_account: [],
    business_url: [],

    amount: { min: null, max: null, type: null },
    bank_name: "all",
    merchant_descriptor: "all",
    country: "all",
    payment_processor: "all",
    alert_id: [],
    transaction_id: [],
    arn: [],
    auth_code: [],
    card_last4: [],
    customer_unsubscribed: "all",
    exclude_fields: [],
    user_visibility: "all",
    paid_status: "all"
  },

  fetchingFilterOptions: false,
  filterOptions: {
    alert_status: [],
    alert_type: [],
    alert_reason: [],
    country: [],
    bank_name: [],
    payment_processor: [],
    customer_unsubscribed: [],
    paid_status: []
  },

  visibleFilters: {
    alert_status: true,
    transaction_id: true,
    alert_timestamp: true,
    alert_service: true,
    card_last4: true
  },
  visibleFiltersAdmin: {
    alert_status: true,
    user_email: true,
    business_account: true,
    alert_timestamp: true
  },

  exportFetching: false,
  exportResult: null,

  saveFetching: false,
  creditRequestSubmitting: false,
  saveResult: null,
  transactionModalSaving: false,
  transactionModalError: null,

  alertDetailsFetching: false,
  alertDetails: null,

  alertLogsFetching: false,
  alertLogs: [],

  alertsViewMode: "table"
}

/* ------------- Shared reducer cases ------------- */

const startFetching = (state) => {
  state.fetching = true
}

// The legacy `alertsFailure` action backed the failure of every list/update/pin request and
// only ever cleared the shared `fetching` flag.
const stopFetching = (state) => {
  state.fetching = false
}

const setSaveFetching = (state) => {
  state.saveFetching = true
}

const stopSaveFetching = (state) => {
  state.saveFetching = false
}

// Shared by `updateAlert` and `updateAlertFromDetailsPage` — both routed through the legacy
// `updateAlertActions.success`, so they produce exactly the same state.
const applyUpdatedAlert = (state, action) => {
  const alert = action.payload

  state.fetching = false
  state.result.results = state.result.results?.map((el) =>
    el.alert_id === alert.alert_id ? { ...el, ...alert } : el
  )
  state.result.attention_list = state.result.attention_list
    ? state.result.attention_list.filter((id) => id !== alert.alert_id)
    : []
  state.saveResult = "alert_update"
}

const slice = createSlice({
  name: ALERTS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `alertsSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `alertsSetProp({ key, value })`,
    // matching the `userSetProp`/`navSetProp` slices.
    alertsSetProp(state, action) {
      const { key, value } = action.payload

      if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearAlertsFilters(state) {
      state.filters = initialState.filters
    },
    resetSaveResult(state) {
      state.saveResult = null
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- statistics / filter options ---------------- */
      .addCase(getAlertsStatistics.pending, (state) => {
        state.fetchingStatistics = true
      })
      .addCase(getAlertsStatistics.fulfilled, (state, action) => {
        state.fetchingStatistics = false
        state.statistics = action.payload
      })
      .addCase(getAlertsStatistics.rejected, (state) => {
        state.fetchingStatistics = false
      })
      .addCase(getAlertsFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getAlertsFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getAlertsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- alerts list ---------------- */
      .addCase(getAlerts.pending, startFetching)
      .addCase(getAlerts.fulfilled, (state, action) => {
        state.fetching = false
        state.result = action.payload
      })
      .addCase(getAlerts.rejected, stopFetching)

      /* ---------------- update / pin ---------------- */
      .addCase(updateAlert.pending, startFetching)
      .addCase(updateAlert.fulfilled, applyUpdatedAlert)
      .addCase(updateAlert.rejected, stopFetching)
      .addCase(updateAlertFromDetailsPage.pending, startFetching)
      .addCase(updateAlertFromDetailsPage.fulfilled, applyUpdatedAlert)
      .addCase(updateAlertFromDetailsPage.rejected, stopFetching)
      .addCase(updateAlertTransaction.pending, (state) => {
        state.transactionModalSaving = true
        state.transactionModalError = null
      })
      .addCase(updateAlertTransaction.fulfilled, (state, action) => {
        const alert = action.payload

        state.transactionModalSaving = false
        state.transactionModalError = null
        state.result.results = state.result.results?.map((el) =>
          el.alert_id === alert.alert_id ? { ...el, ...alert } : el
        )
        if (state.alertDetails?.alert_id === alert.alert_id) {
          state.alertDetails = { ...state.alertDetails, ...alert }
        }
        state.saveResult = "alert_transaction_update"
      })
      .addCase(updateAlertTransaction.rejected, (state, action) => {
        state.transactionModalSaving = false
        state.transactionModalError = action.payload
      })
      .addCase(pinAlert.pending, startFetching)
      .addCase(pinAlert.fulfilled, (state) => {
        state.fetching = false
        state.saveResult = "pin"
      })
      .addCase(pinAlert.rejected, stopFetching)

      /* ---------------- export ---------------- */
      .addCase(getAlertsExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getAlertsExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getAlertsExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- alert details / logs ---------------- */
      .addCase(getAlertDetails.pending, (state) => {
        state.alertDetailsFetching = true
      })
      .addCase(getAlertDetails.fulfilled, (state, action) => {
        state.alertDetailsFetching = false
        state.alertDetails = action.payload
      })
      .addCase(getAlertDetails.rejected, (state) => {
        state.alertDetailsFetching = false
      })
      .addCase(getAlertLogs.pending, (state) => {
        state.alertLogsFetching = true
      })
      .addCase(getAlertLogs.fulfilled, (state, action) => {
        state.alertLogsFetching = false
        state.alertLogs = action.payload
      })
      .addCase(getAlertLogs.rejected, (state) => {
        state.alertLogsFetching = false
      })
      .addCase(getAlertDetailsExport.pending, setSaveFetching)
      .addCase(getAlertDetailsExport.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "alert_details_export"
      })
      .addCase(getAlertDetailsExport.rejected, stopSaveFetching)

      /* ---------------- ticket / credit request ---------------- */
      .addCase(createAlertTicket.pending, setSaveFetching)
      .addCase(createAlertTicket.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = "ticket_create"
        state.alertDetails = { ...state.alertDetails, ...action.payload }
      })
      .addCase(createAlertTicket.rejected, stopSaveFetching)
      .addCase(createCreditRequest.pending, (state) => {
        state.saveFetching = true
        state.creditRequestSubmitting = true
      })
      .addCase(createCreditRequest.fulfilled, (state) => {
        state.saveFetching = false
        state.creditRequestSubmitting = false
        state.saveResult = "credit_request_create"
      })
      .addCase(createCreditRequest.rejected, (state) => {
        state.saveFetching = false
        state.creditRequestSubmitting = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.alerts?.data
        if (!preference || preference.version !== ALERTS_PREFERENCES_VERSION) return
        if (
          !preference.filters ||
          !preference.columns ||
          !preference.columnsAdmin ||
          !preference.visibleFilters ||
          !preference.visibleFiltersAdmin
        )
          return

        state.filters = preference.filters
        state.columns = preference.columns
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFilters = preference.visibleFilters
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
    // `alertsBulkResolve`'s and `bulkUpdateAlertStatus`'s request/success/failure reducers
    // were all no-ops (`state => state`) in the legacy reducer — intentionally unhandled
    // here too. The bulk bar drives its own pending state from the thunk promise.
  }
})

export const alertsActions = slice.actions

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.alerts.filters, initialState.filters)

// Bump when the shape of the saved `{ version, filters, columns, columnsAdmin, visibleFilters,
// visibleFiltersAdmin }` payload changes.
export const ALERTS_PREFERENCES_VERSION = 4

const PERSISTED_REDUCER_VERSION = 3

export const persistedReducer = persistReducer(
  {
    key: "alerts",
    storage,
    // Only the user's own table setup survives a reload — the alerts themselves, their
    // statistics, filter options and the open alert's details are all re-fetched on mount.
    whitelist: [
      "filters",
      "filtersLastUpd",
      "columns",
      "columnsAdmin",
      "visibleFilters",
      "visibleFiltersAdmin",
      "alertsViewMode"
    ],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
